<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="650px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template v-if="isNewPackage">
                        Dodaj pakowanie
                    </template>
                    <template v-else>
                        Edycja pakowania
                    </template>
                </h1>
            </v-card-title>

            <v-card-text>
                <v-row>
                    <v-col cols="12" sm="5">
                        <v-select
                            v-model="packageModel.boxType"
                            :items="boxTypesData"
                            label="Sposób pakowania"
                            item-text="name"
                            return-object
                            outlined
                            dense
                            :error-messages="packageBoxTypeErrors"
                            @change="changeBoxType"
                        />
                    </v-col>
                    <v-col cols="12" sm="7">
                        <v-autocomplete
                            v-model="packageModel.packageType"
                            label="Opakowanie"
                            :items="availablePackages"
                            item-text="name"
                            return-object
                            outlined
                            dense
                            :error-messages="packagePackageTypeErrors"
                            @change="changePackageType"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model="packageModel.length"
                            label="Długość"
                            outlined
                            dense
                            :disabled="!isCustomMadePallet && !isCustomMadeCardBoard"
                            :error-messages="packageLengthErrors"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model="packageModel.width"
                            label="Szerokość"
                            outlined
                            dense
                            :disabled="!isCustomMadePallet && !isCustomMadeCardBoard"
                            :error-messages="packageWidthErrors"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model="packageModel.height"
                            label="Wysokość"
                            outlined
                            dense
                            :disabled="!isCustomMadeCardBoard"
                            :error-messages="packageHeightErrors"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="auto">
                        <v-checkbox
                            v-model="packageModel.setManuallyAmount"
                            class="mt-2"
                            label="Ręczna edycja ilości opakowań"
                            hide-details
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col cols="12" sm="auto">
                        <v-text-field
                            v-model="packageModel.packageAmount"
                            label="Ilość opakowań"
                            type="number"
                            :min="0"
                            outlined
                            dense
                            :disabled="!setAmountManually"
                            :error-messages="packageAmountErrors"
                        />
                    </v-col>
                </v-row>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Anuluj"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    :data-sentry-label="isNewPackage ? 'Dodaj' : 'Zapisz'"
                    @click="onSave"
                >
                    <template v-if="isNewPackage">
                        Dodaj
                    </template>
                    <template v-else>
                        Zapisz
                    </template>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { isEmpty } from 'lodash'
import { required, minValue, integer } from 'vuelidate/lib/validators'

export default {
    name: 'PackageDialog',
    props: {
        isOpen: {
            type: Boolean
        },
        packageData: {
            type: Object,
            default: null
        },
        boxTypesData: {
            type: Array,
            default: null
        },
        packageTypesData: {
            type: Array,
            default: null
        },
    },
    data() {
        return {
            packageModel: {
                boxTypeId: null,
                boxType: null,
                packageType: null,
                packageTypeId: null,
                packageAmount: 0,
                length: null,
                width: null,
                height: null,
                calculationImpositionTaskPackageWares: [],
                setManuallyAmount: false
            },
            packageTypes: [],
            availablePackages: []
        }
    },
    validations() {
        const rules = {
            packageModel: {
                boxTypeId: {
                    required
                },
                packageTypeId: {
                    required
                }
            }
        }

        if (this.isCustomMadePallet || this.isCustomMadeCardBoard) {
            rules.packageModel['length'] = {
                required,
                minValue: minValue(1)
            }

            rules.packageModel['width'] = {
                required,
                minValue: minValue(1)
            }
        }
        if (this.isCustomMadeCardBoard) {
            rules.packageModel['height'] = {
                required,
                minValue: minValue(1)
            }
        }

        if (this.setAmountManually) {
            rules.packageModel['packageAmount'] = {
                required,
                minValue: minValue(1),
                integer
            }
        }

        return rules
    },
    computed: {
        isNewPackage() {
            return isEmpty(this.packageData)
        },
        isCustomMadePallet() {
            return this.packageModel.packageTypeId === 34
        },
        isCustomMadeCardBoard() {
            return this.packageModel.packageTypeId === 46
        },
        setAmountManually() {
            return this.packageModel.setManuallyAmount
        },
        filteredPackageTypes() {
            return this.packageTypes.filter(item =>
                item.boxType.id === this.packageModel.boxTypeId &&
                (
                    (this.packageModel.packageTypeId !== null && item.id === this.packageModel.packageTypeId) ||
                    item.isVisible
                )
            )
        },

        // Walidacje
        packageBoxTypeErrors() {
            const errors = []

            if (!this.$v.packageModel.boxTypeId.$dirty) return errors
            !this.$v.packageModel.boxTypeId.required && errors.push('Proszę podać sposób pakowania.')

            return errors
        },
        packagePackageTypeErrors() {
            const errors = []

            if (!this.$v.packageModel.packageTypeId.$dirty) return errors
            !this.$v.packageModel.packageTypeId.required && errors.push('Proszę podać rodzaj opakowania.')

            return errors
        },
        packageLengthErrors() {
            const errors = []

            if (!this.isCustomMadePallet && !this.isCustomMadeCardBoard) return errors

            if (!this.$v.packageModel.length.$dirty) return errors

            if (!this.$v.packageModel.length.required || !this.$v.packageModel.length.minValue) {
                errors.push('Proszę podać długość.')
            }

            return errors
        },
        packageWidthErrors() {
            const errors = []

            if (!this.isCustomMadePallet && !this.isCustomMadeCardBoard) return errors

            if (!this.$v.packageModel.width.$dirty) return errors

            if (!this.$v.packageModel.width.required || !this.$v.packageModel.width.minValue) {
                errors.push('Proszę podać szerokość.')
            }

            return errors
        },
        packageHeightErrors() {
            const errors = []

            if (!this.isCustomMadeCardBoard) return errors

            if (!this.$v.packageModel.height.$dirty) return errors

            if (!this.$v.packageModel.height.required || !this.$v.packageModel.height.minValue) {
                errors.push('Proszę podać wysokość.')
            }

            return errors
        },
        packageAmountErrors() {
            const errors = []

            if (!this.setAmountManually) return errors

            if (!this.$v.packageModel.packageAmount.required) {
                errors.push('Liczba opakowań jest wymagana')
            }

            if (!this.$v.packageModel.packageAmount.minValue || !this.$v.packageModel.packageAmount.integer) {
                errors.push('Proszę podać całkowitą dodatnią liczbę opakowań')
            }

            return errors
        },
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                if (!isEmpty(this.packageData)) {
                    this.packageModel = JSON.parse(JSON.stringify(this.packageData))
                }

                this.packageTypes = this.packageTypesData.filter(item => item.boxType.id === this.packageModel.boxTypeId)
                this.setAvailablePackages()
            } else {
                setTimeout(() => {
                    this.$v.$reset()
                    this.packageTypes = []
                    this.availablePackages = []
                    this.packageModel = {
                        boxTypeId: null,
                        boxType: null,
                        packageType: null,
                        packageTypeId: null,
                        packageAmount: 0,
                        length: null,
                        width: null,
                        height: null,
                        calculationImpositionTaskPackageWares: []
                    }
                }, 500)
            }
        }
    },
    methods: {
        onSave() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            const packageModel = JSON.parse(JSON.stringify(this.packageModel))

            this.$emit('on-save', packageModel, this.isNewPackage)
        },
        onClose() {
            this.$emit('on-close')
        },
        changeBoxType(boxType) {
            this.packageModel.boxTypeId = boxType.id
            this.packageTypes = this.packageTypesData.filter(item => item.boxType.id === boxType.id)
            this.packageModel.packageType = null
            this.packageModel.packageTypeId = null
            this.setAvailablePackages()
        },
        changePackageType(packageType) {
            this.packageModel.packageTypeId = packageType.id
            this.packageModel.length = packageType.length
            this.packageModel.width = packageType.width
            this.packageModel.height = packageType.height
            this.setAvailablePackages()
        },
        setAvailablePackages() {
            // available means all "visible" + selected (if selected and invisible)
            const visible = this.filteredPackageTypes.filter(item => item.isVisible === true)
            const selected = this.packageModel.packageType

            this.availablePackages = [...visible]

            if (selected && selected.isVisible === false) {
                const exists = this.availablePackages.some(p => p.id === selected.id)

                if (!exists) this.availablePackages.push(selected)
            }
        },
    }
}
</script>